import React from 'react'
import {
    Box,
    Typography,
    Card,
    CardContent,
    Chip,
    Avatar,
    Divider,
    CircularProgress,
    makeStyles,
    Button,
} from "@material-ui/core";
import { Appeal } from './FlaggedContent';
import defaultAvatars from "../../../../../assets/defaultAvatars.svg";
import { Link } from 'react-router-dom';

export const useStyles = makeStyles(theme => ({
    contentCard: {
        backgroundColor: "#1a1a1a",
        border: "1px solid #333",
        marginBottom: theme.spacing(2),
        color: "#fff",
        "&:hover": {
            borderColor: "#FE5A97",
        },
    },
    contentHeader: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: theme.spacing(1),
    },
    userInfo: {
        display: "flex",
        alignItems: "center",
        gap: theme.spacing(1),
    },
    contentPreview: {
        maxWidth: "200px",
        maxHeight: "200px",
        borderRadius: "8px",
        objectFit: "cover",
    },
    contentPreviewContainer: {
        marginTop: theme.spacing(2),
        padding: theme.spacing(2),
        backgroundColor: "#2a2a2a",
        borderRadius: "8px",
        border: "1px solid #444",
    },
    contentPreviewImage: {
        maxWidth: "100%",
        maxHeight: "400px",
        borderRadius: "8px",
        objectFit: "contain",
        marginBottom: theme.spacing(1),
    },
    contentPreviewVideo: {
        maxWidth: "100%",
        maxHeight: "400px",
        borderRadius: "8px",
        marginBottom: theme.spacing(1),
    },
    contentPreviewText: {
        padding: theme.spacing(1.5),
        backgroundColor: "#1a1a1a",
        borderRadius: "4px",
        border: "1px solid #333",
        whiteSpace: "pre-wrap",
        wordBreak: "break-word",
    },
    postStatusBadge: {
        marginLeft: theme.spacing(1),
    },
    expandButton: {
        marginTop: theme.spacing(1),
        color: "#FE5A97",
        textTransform: "none",
    },
    actionButtons: {
        display: "flex",
        gap: theme.spacing(1),
    },
    approveButton: {
        backgroundColor: "#4caf50",
        color: "#fff",
        "&:hover": {
            backgroundColor: "#45a049",
        },
    },
    rejectButton: {
        backgroundColor: "#f44336",
        color: "#fff",
        "&:hover": {
            backgroundColor: "#da190b",
        },
    },
    statusSelect: {
        minWidth: 170
    }
}));

type AppealCardProps = {
    appeal: Appeal,
    loadingAvatars: {
        [key: string]: boolean;
    },
    avatarUrls: { [key: string]: string },
    loadAppeals: () => Promise<void>,
    onClickLinkAppeal: (appeal: Appeal, type: "post" | "profile" | "comment") => void,
    setAppealStatusDialog: React.Dispatch<React.SetStateAction<{
        open: boolean;
        appeal: Appeal | null;
        status: string;
        reviewNotes: string;
    }>>
}

// type AppealStatus = "pending" | "reviewing" | "approved" | "rejected"

const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    });
};

export default function AppealCard({ appeal, loadingAvatars, avatarUrls, loadAppeals, onClickLinkAppeal, setAppealStatusDialog }: AppealCardProps) {
    const classes = useStyles();

    // const handleQuickUpdateAppealStatus = async (
    //     appeal: Appeal,
    //     status: AppealStatus,
    // ) => {
    //     try {
    //         const response = await updateAppealStatusAPI(appeal._id, status, "");

    //         if (response.success) {
    //             showToast(`Appeal status updated to ${status}`, "success");
    //             loadAppeals();
    //         }
    //     } catch (error) {
    //         console.error("Error updating appeal status:", error);
    //         showToast("Error updating appeal status", "error");
    //     }
    // };

    return (
        <Card key={appeal._id} className={classes.contentCard}>
            <CardContent>
                <Box className={classes.contentHeader}>
                    <Box className={classes.userInfo}>
                        {loadingAvatars[appeal.user?._id || ""] ? (
                            <Avatar>
                                <CircularProgress size={24} />
                            </Avatar>
                        ) : (
                            <Avatar
                                src={
                                    appeal.user?._id && avatarUrls[appeal.user._id]
                                        ? avatarUrls[appeal.user._id]
                                        : appeal.user?.profileImg
                                            ? appeal.user.profileImg
                                            : defaultAvatars
                                }
                                alt={appeal.user?.username || "User"}
                            >
                                {!appeal.user?.profileImg &&
                                    !avatarUrls[appeal.user?._id || ""] &&
                                    (appeal.user?.firstName?.[0] ||
                                        appeal.user?.username?.[0] ||
                                        "U")}
                            </Avatar>
                        )}
                        <Box>
                            <Typography variant="h6" style={{ color: "#fff" }}>
                                {appeal.user?.username ? (
                                    <Link
                                        to={`/${appeal.user.username}`}
                                        style={{
                                            color: "#FE5A97",
                                            textDecoration: "none",
                                        }}
                                        onMouseEnter={e => {
                                            e.currentTarget.style.textDecoration =
                                                "underline";
                                        }}
                                        onMouseLeave={e => {
                                            e.currentTarget.style.textDecoration = "none";
                                        }}
                                    >
                                        {appeal.user.username}
                                    </Link>
                                ) : (
                                    appeal.user?.firstName || "Unknown User"
                                )}
                            </Typography>
                            <Typography variant="body2" style={{ color: "#ccc" }}>
                                {appeal.user?.email || "No email available"}
                            </Typography>
                        </Box>
                    </Box>
                    <Box
                        display="flex"
                        alignItems="center"
                        style={{ gap: "8px" }}
                    >
                        <Chip
                            label={appeal.status.toUpperCase()}
                            variant="outlined"
                            size="small"
                            style={{
                                color: "#fff",
                                borderColor:
                                    appeal.status === "pending"
                                        ? "#ff9800"
                                        : appeal.status === "reviewing"
                                            ? "#2196f3"
                                            : appeal.status === "approved"
                                                ? "#4caf50"
                                                : "#f44336",
                            }}
                        />
                    </Box>
                </Box>

                <Divider
                    style={{ backgroundColor: "#333", margin: "16px 0" }}
                />

                <Box>
                    <Typography
                        variant="body1"
                        style={{ marginBottom: "8px", color: "#fff" }}
                    >
                        <strong>{appeal.post ? "Post ID" : appeal.profile ? "Profile ID" : "Comment ID"}:</strong>{" "}
                        <span
                            onClick={() => onClickLinkAppeal(appeal, appeal.targetType)}
                            style={{
                                color: "#FE5A97",
                                cursor: "pointer",
                                textDecoration: "underline",
                            }}
                            onMouseEnter={e => {
                                e.currentTarget.style.color = "#e04a82";
                            }}
                            onMouseLeave={e => {
                                e.currentTarget.style.color = "#FE5A97";
                            }}
                        >
                            {appeal.targetId}
                        </span>
                    </Typography>
                    <Typography
                        variant="body2"
                        className={classes.contentPreviewText}
                        style={{ color: "#fff", marginTop: "8px" }}
                    >
                        <strong>Appeal Comment:</strong> {appeal.commentText}
                    </Typography>
                    {appeal.reviewNotes && (
                        <Typography
                            variant="body2"
                            className={classes.contentPreviewText}
                            style={{ color: "#fff", marginTop: "8px" }}
                        >
                            <strong>Review Notes:</strong> {appeal.reviewNotes}
                        </Typography>
                    )}
                    <Typography variant="body2" style={{ color: "#ccc" }}>
                        <strong>Appealed:</strong> {formatDate(appeal.createdAt)}
                    </Typography>
                    {appeal.reviewedAt && (
                        <Typography variant="body2" style={{ color: "#ccc" }}>
                            <strong>Reviewed:</strong>{" "}
                            {formatDate(appeal.reviewedAt)}
                        </Typography>
                    )}
                </Box>

                <Box
                    className={classes.actionButtons}
                    style={{ marginTop: "16px" }}
                >
                    <Button
                        variant="contained"
                        className={classes.approveButton}
                        onClick={() =>
                            setAppealStatusDialog({
                                open: true,
                                appeal,
                                status: appeal.status || "open",
                                reviewNotes: "",
                            })
                        }
                    >
                        Change Status
                    </Button>
                </Box>
            </CardContent>
        </Card>
    )
}
